import React, { useCallback, useState } from 'react';

import MaterialIcon from '@/components/Icon/MaterialIcon';

interface CollapsibleSectionProps {
  id: string;
  children: (props: { collapsed: boolean; collapseButton: React.ReactNode }) => React.ReactNode;
  defaultCollapsed?: boolean;
  /** Screen-reader label for the scroll/observer target (ENG-5256 scopes). */
  'data-screen-label'?: string;
}

function CollapseButton({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}): React.ReactNode {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={collapsed ? 'Expand section' : 'Collapse section'}
      aria-label={collapsed ? 'Expand section' : 'Collapse section'}
      className="-ml-4 flex h-[2.4rem] w-[2.4rem] cursor-pointer items-center justify-center rounded-sm bg-transparent text-on-surface-muted transition-colors hover:bg-surface-elevated"
    >
      <MaterialIcon icon={collapsed ? 'chevron_right' : 'expand_more'} />
    </button>
  );
}

export function CollapsibleSection({
  id,
  children,
  defaultCollapsed = false,
  'data-screen-label': dataScreenLabel,
}: CollapsibleSectionProps): React.ReactNode {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const toggle = useCallback(() => setCollapsed((prev) => !prev), []);

  const collapseButton = <CollapseButton collapsed={collapsed} onToggle={toggle} />;

  return (
    <section id={id} tabIndex={-1} data-screen-label={dataScreenLabel} className="scroll-mt-80">
      {children({ collapsed, collapseButton })}
    </section>
  );
}
